// Affixes pool editor — flat list of (type, subType, min, max) rows.
// Simpler than the other catalogues since each entry has no nested data.

import type { AffixDefinition } from "@vinyl/shared";
import { el } from "./dom.js";

const TYPES = ["stat", "dmgPct", "hp", "critPct", "mana", "resistPct"] as const;
type AffixType = (typeof TYPES)[number];

// M8 — single mana pool (no subType), 7 resists (physical + 6 schools).
const SUBTYPES_FOR: Record<AffixType, readonly string[] | null> = {
  stat: ["str", "dex", "con", "int", "fai", "rap"],
  dmgPct: ["melee", "ranged", "spell"],
  hp: null,
  critPct: null,
  mana: null,
  resistPct: [
    "physical",
    "divine",
    "necromancy",
    "water",
    "fire",
    "air",
    "earth",
  ],
};

export interface AffixesScreenCallbacks {
  onDirtyChange: (dirty: boolean) => void;
}

export interface AffixesScreen {
  root: HTMLElement;
  setAffixes(pool: readonly AffixDefinition[]): void;
  getDraft(): AffixDefinition[];
  isDirty(): boolean;
  markPublished(): void;
}

interface DraftRow extends AffixDefinition {
  __isDirty?: boolean;
  __isNew?: boolean;
}

export function createAffixesScreen(cb: AffixesScreenCallbacks): AffixesScreen {
  let drafts: DraftRow[] = [];

  const tableBody = el("div", { class: "affix-pool-body" });

  function markDirty(): void {
    cb.onDirtyChange(true);
  }

  function render(): void {
    tableBody.replaceChildren();
    for (let idx = 0; idx < drafts.length; idx++) {
      tableBody.append(renderRow(idx, drafts[idx]!));
    }
    if (drafts.length === 0) {
      tableBody.append(
        el("div", { class: "affix-pool-empty" }, "Pool is empty. Add at least one entry to publish."),
      );
    }
  }

  function renderRow(idx: number, row: DraftRow): HTMLElement {
    const subtypes = SUBTYPES_FOR[row.type];
    const wrapper = el(
      "div",
      { class: "affix-pool-row" + (row.__isNew ? " created" : row.__isDirty ? " dirty" : "") },
    );

    // Type
    const typeSel = el(
      "select",
      {
        onchange: (e: Event) => {
          const newType = (e.target as HTMLSelectElement).value as AffixType;
          const allowed = SUBTYPES_FOR[newType];
          drafts[idx] = {
            ...row,
            type: newType,
            subType: allowed ? (allowed[0] as AffixDefinition["subType"]) : undefined,
            __isDirty: true,
          };
          markDirty();
          render();
        },
      },
    );
    for (const t of TYPES) {
      const opt = el("option", { value: t }, t);
      if (t === row.type) (opt as HTMLOptionElement).selected = true;
      typeSel.append(opt);
    }
    wrapper.append(typeSel);

    // SubType
    if (subtypes) {
      const sel = el(
        "select",
        {
          onchange: (e: Event) => {
            drafts[idx] = {
              ...row,
              subType: (e.target as HTMLSelectElement).value as AffixDefinition["subType"],
              __isDirty: true,
            };
            markDirty();
          },
        },
      );
      for (const s of subtypes) {
        const opt = el("option", { value: s }, s);
        if (s === row.subType) (opt as HTMLOptionElement).selected = true;
        sel.append(opt);
      }
      wrapper.append(sel);
    } else {
      wrapper.append(el("span", { class: "affix-no-sub" }, "—"));
    }

    // Min
    wrapper.append(
      numInput(row.min, (v) => {
        drafts[idx] = { ...row, min: v, __isDirty: true };
        markDirty();
      }),
    );

    // Max
    wrapper.append(
      numInput(row.max, (v) => {
        drafts[idx] = { ...row, max: v, __isDirty: true };
        markDirty();
      }),
    );

    // Remove
    wrapper.append(
      el(
        "button",
        {
          class: "button danger",
          type: "button",
          title: "Remove",
          onclick: () => {
            drafts.splice(idx, 1);
            markDirty();
            render();
          },
        },
        "×",
      ),
    );

    return wrapper;
  }

  function numInput(value: number, onChange: (v: number) => void): HTMLInputElement {
    return el("input", {
      type: "number",
      value: String(value),
      step: "1",
      oninput: (e: Event) => {
        const raw = (e.target as HTMLInputElement).value;
        const v = Number.parseInt(raw, 10);
        if (Number.isFinite(v)) onChange(v);
      },
    }) as HTMLInputElement;
  }

  const header = el(
    "div",
    { class: "affix-pool-header" },
    el(
      "div",
      { class: "affix-pool-row" },
      el("span", { class: "label" }, "Type"),
      el("span", { class: "label" }, "SubType"),
      el("span", { class: "label" }, "Min"),
      el("span", { class: "label" }, "Max"),
      el("span", {}, ""),
    ),
  );

  const addBtn = el(
    "button",
    {
      class: "button secondary",
      type: "button",
      onclick: () => {
        drafts.push({ type: "stat", subType: "str", min: 1, max: 3, __isNew: true });
        markDirty();
        render();
      },
    },
    "+ Add entry",
  );

  const root = el(
    "div",
    { class: "affix-pool-layout" },
    el(
      "div",
      { class: "editor-section" },
      el("h3", {}, "Affix pool"),
      el(
        "div",
        { class: "affix-hint" },
        "Rare item drops pick 1–3 unique entries from this pool and roll a value within [min, max].",
      ),
      header,
      tableBody,
      el("div", { class: "affix-actions" }, addBtn),
    ),
  );

  return {
    root,
    setAffixes(pool) {
      drafts = pool.map((p) => ({ ...p }));
      cb.onDirtyChange(false);
      render();
    },
    getDraft() {
      return drafts.map(({ __isDirty: _d, __isNew: _n, ...rest }) => rest);
    },
    isDirty() {
      return drafts.some((r) => r.__isDirty || r.__isNew);
    },
    markPublished() {
      for (const r of drafts) {
        delete r.__isNew;
        delete r.__isDirty;
      }
      cb.onDirtyChange(false);
      render();
    },
  };
}
